// Generated by scripts/build-rules-content.mjs from country-uk.json (aligned 9 Oct 2026).
// Content for sales: plain sentences, no em dash, no person or company names.
// Edit here or regenerate; tests/rules-explainer.test.mjs checks the writing rules.
import type { CountryGuide } from "../types";

export const GUIDE_UNITED_KINGDOM: CountryGuide = {
  id: "united-kingdom",
  code: "uk",
  title: "United Kingdom",
  shortName: "the United Kingdom",
  entities: ["uk"],
  areaRoles: ["uk"],
  live: true,
  cardLine:
    "Quotes in GBP with carriage by order value, a minimum order value of GBP 225, and about 28 customers quoted from Germany.",
  summary:
    "The UK team quotes Wheelabrator and StrikoWestofen spare parts in GBP from one shared mailbox. The UK was the most experienced Go Autonomous country, and most of the rules below come from what UK sales corrected by hand: carriage by order value, no VAT for Northern Ireland and abroad, a minimum order value of GBP 225, a sales pool that must come from the part and never default to Impact, and about 28 customers that must be quoted from Germany. Norican I2Q runs in shadow mode: it prepares, you decide and you send. Check every UK line before you use it.",
  howInquiriesArrive: [
    'Inquiries come into the shared mailbox UK internal sales (UK_intsales), about 1,500 mails a month, plus about 15 older addresses that forward into it. Norican I2Q reads a copy of that mailbox. Every mail is sorted first: only requests for a price or a quotation become a case in "To review". Purchase orders are parked in their own view, and spam, courier notices (about 1 in 20 UK mails), invoices and delivery questions are listed under "Not an inquiry". Mails forwarded by a Norican colleague are traced back to the original customer. Nothing is sent: you copy the reply text and create the quotation in F&O as today.',
  ],
  specialities: [
    {
      id: "uk-mailbox-sorting",
      title: "Only inquiries reach your list",
      status: "active",
      tone: "info",
      topic: "mail-and-case",
      checks:
        "Every mail in the UK mailbox is sorted into inquiry, purchase order, order status, invoice, delivery change, complaint or other.",
      why: "In the UK about 15 % of mail was spam and about 5 % courier notices, and Go Autonomous wasted work on them.",
      youSee:
        'Info. Inquiries appear in "To review". Purchase orders sit in the parked view, everything else under "Not an inquiry".',
      whatToDo:
        'If an inquiry ended up in the wrong place, open it from the parked view or "Not an inquiry" and quote it by hand as today. Then use "Give feedback", so the sorting gets better.',
      example:
        'A customer writes "Please send your best price for 20 off T962243" and it lands in "To review". A courier\'s delivery notice lands under "Not an inquiry".',
      registryIds: [],
      glossaryIds: ["scope-inquiries-only"],
      source:
        "docs/scope.md; goa-knowledge.md Part A section 3 UK and section 4 (UK blocked senders list)",
    },
    {
      id: "uk-mixed-po-and-inquiry",
      title: "Purchase orders that are really a price question",
      status: "active",
      tone: "info",
      topic: "mail-and-case",
      checks:
        "A mail first read as a purchase order gets a second look. If it also asks for a price, it stays an inquiry.",
      why: "Some mails sorted as orders really ask for a price, so they must not get lost.",
      youSee: 'Info. Most mixed mails stay in "To review". Some still end up in the parked view.',
      whatToDo:
        "Glance at the parked view now and then. If a customer asks for a price there, quote it by hand.",
      example:
        'A PO for 4 lines that also says "and please quote 2 spare wear plates" stays in "To review".',
      registryIds: [],
      glossaryIds: ["scope-inquiries-only"],
      source: "docs/scope.md (known gap: inquiries labelled as orders)",
    },
    {
      id: "uk-agents",
      title: "Agents and distributors: quote the agent first, show the end customer",
      status: "active",
      tone: "info",
      topic: "customer",
      checks:
        "Which account the quotation is for when an agent or a distributor writes on behalf of an end customer.",
      why: "The quotations of UK sales on such mails are mostly on the account of the agent. The product owner decided on 14 October 2026, from that analysis and for now, that the UK quotes the agent first.",
      youSee:
        'Info. The card shows both parties. The agent carries "Quoted first", the end customer stands next to it. An account of the agent for this end customer comes first. This is always a suggestion.',
      whatToDo:
        'Make the quotation on the account with "Quoted first" and send it to the agent. When the mail asks for the end customer\'s account, pick that one.',
      example:
        "A distributor asks for 10 blast wheel liners for a foundry it supplies. I2Q puts the distributor's account first and shows the foundry next to it.",
      registryIds: [],
      glossaryIds: ["agents-and-distributors"],
      source: "docs/customer-matching-policy.md (product owner, 14 October 2026)",
    },
    {
      id: "uk-existing-customer-only",
      title: "Quotations go to existing customer accounts",
      status: "active",
      tone: "info",
      topic: "customer",
      checks:
        "The sender's e-mail address is looked up as a contact on a UK customer account in F&O, then the signature and the company domain.",
      why: "Price, margin and the whole quotation depend on the right account, and in the UK only existing accounts get a quotation in F&O.",
      youSee:
        'Info. The customer is either filled in as certain, or shown as a suggestion with "please confirm". With no match you see no customer.',
      whatToDo:
        "Confirm or change the suggested customer. For a new company with no account, send a pro-forma quotation in Excel as today. The account is created only after a PO or payment.",
      example:
        "A buyer writes from a new address at a known foundry. I2Q suggests the foundry's account and you confirm it.",
      registryIds: [],
      glossaryIds: ["customer-finding"],
      source: "goa-knowledge.md Part A 2.1; Part B UK (new parties); code mail/customer_rules.py",
    },
    {
      id: "uk-quote-from-germany",
      title: "Customers that must be quoted from Germany",
      status: "active",
      tone: "warning",
      topic: "customer",
      checks:
        "About 28 customers write to the UK mailbox but hold accounts in both the UK and Wheelabrator Germany (WGG). I2Q checks the UK account against that list.",
      why: 'Go Autonomous had a rule "create in WGG" for these customers, because quoting them from the UK gives the wrong company, prices and owner.',
      youSee:
        'Warning: "This customer is quoted from Wheelabrator Germany, account ...", with the German account number. I2Q does not change the customer on the case.',
      whatToDo:
        "Pass the inquiry to Wheelabrator Germany, which quotes this customer. Do not quote it from the UK.",
      example:
        "A customer on the WGG list asks the UK mailbox for 10 blast wheel blades. You hand the inquiry to Germany.",
      registryIds: ["gate.uk_wgg"],
      glossaryIds: [],
      modes: {
        "gate.uk_wgg": {
          uk: "warn",
        },
      },
      source:
        "goa-knowledge.md Part A 2.1 (UK mailbox, WGG customer; NG-60), backlog M10; code mail/customer_rules.py (other entity only a suggestion)",
    },
    {
      id: "uk-export-customers",
      title: "Export customers",
      status: "active",
      tone: "warning",
      topic: "customer",
      checks:
        "Whether the UK customer account is in the export customer group (EXP). An account without a customer group counts as not known, not as export.",
      why: "Go Autonomous first left UK export customers out, and the agreed answer is to flag them, not to stop them.",
      youSee: 'Warning: "This customer is in the export customer group."',
      whatToDo:
        "Check the export terms and documents before you quote. Set delivery terms, VAT and carriage yourself, as today.",
      example:
        "A customer in Turkey with UK customer group EXP asks for a quotation. You set the export delivery terms by hand.",
      registryIds: ["gate.uk_export"],
      glossaryIds: [],
      modes: {
        "gate.uk_export": {
          uk: "warn",
        },
      },
      source:
        "goa-knowledge.md Part A 2.1 (Export customers UK, Customer groups), 2.5 (EXP delivery terms, NG-41), gap G7",
    },
    {
      id: "uk-competitors-blocked-countries",
      title: "Competitors and blocked countries",
      status: "active",
      tone: "blocked",
      topic: "customer",
      checks:
        "Mail from a competitor's domain or from a sanctioned country, such as Russia, Belarus or Iran.",
      why: "Norican quotes competitors higher or not at all and must not quote sanctioned countries, so Go Autonomous sent both to manual handling.",
      youSee:
        'Blocked: "The sender\'s domain is on the competitor list." The case is held and the queue shows this reason first. Blocked: "The sender\'s mail domain belongs to Russia, which is on the sanctions list."',
      whatToDo:
        "Quote a competitor only if your manager agrees. Do not quote a sanctioned country; pass it to your manager for an export control check.",
      example:
        "An inquiry from a .ru address. The case is held with the sanctions reason, and you do not quote it.",
      registryIds: ["gate.competitor", "gate.sanctioned"],
      glossaryIds: ["competitor-domains", "sanctioned-countries"],
      modes: {
        "gate.competitor": {
          uk: "block",
        },
        "gate.sanctioned": {
          uk: "block",
        },
      },
      source: "goa-knowledge.md Part A 1 (point 6), 2.1 (Competitor domains, Sanctioned countries)",
    },
    {
      id: "uk-credit-block",
      title: "Customer on credit block",
      status: "planned",
      tone: "info",
      topic: "customer",
      checks:
        "Whether the customer account is on stop in F&O, and whether that block matters for a quotation.",
      why: "In Go Autonomous UK customers blocked only for invoicing were wrongly blocked for quotations too, and the notice was unclear.",
      youSee:
        'Planned: an info note "account on block". It never stops the quotation, because a credit block stops the order, not the quote.',
      whatToDo: "You can still quote. Tell credit control as today if the customer wants to order.",
      example:
        "A customer on invoice block asks for a price. You quote it and mention the block to credit control.",
      registryIds: [],
      glossaryIds: ["prospect-and-credit-block"],
      source:
        "goa-knowledge.md Part A 2.1 (Credit block), 9.1 (#86, #93); Part B UK (credit control mail)",
    },
    {
      id: "uk-part-numbers",
      title: "UK part numbers and customer part numbers",
      status: "active",
      tone: "info",
      topic: "items",
      checks:
        'Part numbers from the mail are repaired before matching: a missing "T" prefix, dashes, spaces, upper and lower case. Customer part numbers on file are translated to our item.',
      why: 'Go Autonomous only searched "begins with", so UK parts written without their "T" prefix were not found.',
      youSee:
        'Info. Each line shows "Matched", "Suggested, please confirm", "Needs a check" or "Not evaluated, check by hand".',
      whatToDo:
        'Check every suggested item. Pick the right one where I2Q offers several. Words such as "Wheelabrator" in the mail are never an item.',
      example: 'The customer writes "962243". I2Q suggests T962243 and you confirm it.',
      registryIds: [],
      glossaryIds: ["customer-item-numbers"],
      source:
        "goa-knowledge.md Part A 2.2 (Part-number repair, Customer item numbers: UK 391 rows); Part B 3 (UK part needed a T prefix)",
    },
    {
      id: "uk-blocked-replaced-items",
      title: "Blocked and replaced items",
      status: "active",
      tone: "blocked",
      topic: "items",
      checks:
        "The item status in F&O. Blocked items are not quoted. Items being phased out point to their successor.",
      why: "The UK has about 3,270 items with an alternative, and Go Autonomous sometimes went the wrong way between old and new item.",
      youSee:
        'Blocked: "Blocked for sale in F&O" on the line. Warning: "Phase-out item", with the replacement number when F&O has one. Warning: "Line 3: use 654321, it replaces 123456."',
      whatToDo: "Quote the successor and tell the customer it replaces the number they asked for.",
      example:
        'A phased-out bearing has a successor. You quote the successor with the note "replaces the item you asked for".',
      registryIds: ["gate.signal_review", "gate.successor"],
      glossaryIds: ["signal-codes", "successor-items"],
      modes: {
        "gate.signal_review": {
          uk: "warn",
        },
        "gate.successor": {
          uk: "warn",
        },
      },
      source:
        "goa-knowledge.md Part A 2.2 (Signal codes, Alternatives and successors); code pricing/lookup.py",
    },
    {
      id: "uk-new-revised-items",
      title: "New and revised items need a look",
      status: "active",
      tone: "warning",
      topic: "items",
      checks:
        'UK items whose signal code needs a check, such as NEW, REV, ECO, PRO-UPDATE or NOMOVER. They are not yet a plain "OK" item.',
      why: "Go Autonomous quoted these silently, and a revised item needs a person to check it.",
      youSee:
        'Warning on the line: "Line 2 has signal code REV, which needs a check before quoting."',
      whatToDo: "Check price and drawing state with the product team before quoting.",
      example:
        "A line matches an item with code REV. You confirm the current revision before you quote.",
      registryIds: ["gate.signal_review"],
      glossaryIds: ["signal-codes"],
      modes: {
        "gate.signal_review": {
          uk: "warn",
        },
      },
      source: "goa-knowledge.md Part A 2.2 (Signal codes: UK top codes), backlog M6",
    },
    {
      id: "uk-warehouse",
      title: "Standard warehouse only",
      status: "planned",
      tone: "warning",
      topic: "items",
      checks:
        "The warehouse on the line. In the UK only the standard warehouse (002) is quoted without review.",
      why: "In Go Autonomous a line in another warehouse had to be handled by hand.",
      youSee: "Planned: a warning on the line when the warehouse is not the standard one.",
      whatToDo: "Check where the part really is before you give a delivery time.",
      example: "A line shows a project warehouse. You check stock and delivery time yourself.",
      registryIds: [],
      glossaryIds: [],
      source: "goa-knowledge.md Part A 2.2 (Warehouse), Part B 3 (UK: only 002), backlog S6",
    },
    {
      id: "uk-price-from-price-list",
      title: "Prices come from the customer's price list",
      status: "active",
      tone: "info",
      topic: "price",
      checks:
        "The price comes from the customer's price list in F&O (F&O calls it a trade agreement). That is the customer's own price list if there is one, else the customer's price group. The price must be valid today and fit the quantity.",
      why: "Prices in Go Autonomous were read-only from F&O, and a missing price stopped the quotation.",
      youSee:
        'Info: the price is filled in per line. Blocked: the line shows "No price", and the prepared quotation says "No price was found for ...". The other lines still show. I2Q shows the list price, not a discount you give by hand.',
      whatToDo:
        "For a line without a price, ask Czechia for a price as today. Never send a zero price.",
      example:
        "A line for a Czech-made part has no price. You ask the Czech team, then add the price yourself.",
      registryIds: ["pricing.no_price"],
      glossaryIds: ["never-zero-price"],
      modes: {
        "pricing.no_price": {
          uk: "block",
        },
      },
      source:
        "goa-knowledge.md Part A 2.3 (Price source, No price: FR/UK request price from CZ); code pricing/lookup.py",
    },
    {
      id: "uk-expired-price",
      title: "Expired price",
      status: "active",
      tone: "blocked",
      topic: "price",
      checks: "Whether the price list for the item has run out.",
      why: "In the UK a ship date that jumps to today means the Czech price list has expired, and Go Autonomous should not have quoted it.",
      youSee:
        'Blocked: the line shows "Price expired" and the day the last price ended. The prepared quotation is held until the line has a valid price.',
      whatToDo: "Ask for a new price before you quote. Do not reuse the old one.",
      example:
        "A wear part from Czechia shows an expired price. You ask for an updated price first.",
      registryIds: ["pricing.no_price"],
      glossaryIds: ["price-age"],
      modes: {
        "pricing.no_price": {
          uk: "block",
        },
      },
      source:
        "goa-knowledge.md Part A 2.3 (Expired / stale price), Part B UK (Price); code pricing/lookup.py (status expired)",
    },
    {
      id: "uk-margin",
      title: "Margin check",
      status: "active",
      tone: "blocked",
      topic: "price",
      checks:
        'Where cost is known, the margin of each line (shown as "Contribution ratio") is compared with the minimum margin set for the UK.',
      why: "In the UK Go Autonomous treated a margin below the minimum as an error, so low prices did not go out unseen.",
      youSee:
        'Blocked until a manager approves: the line shows "Margin: needs approval". If F&O has no cost for the item, the line shows "Margin: not checked (no cost in F&O)", and the prepared quotation is held as well. Only colleagues with margin rights see the figures. Everyone else sees the result only.',
      whatToDo:
        'Check the price. If it is right, ask your manager to release it with "Approve price". If the item, price, quantity or customer changes later, the approval must be given again.',
      example:
        "A large repeat order of liners comes out below the UK minimum margin. Your manager checks it and approves the price.",
      registryIds: ["pricing.margin"],
      glossaryIds: ["contribution-ratio"],
      modes: {
        "pricing.margin": {
          uk: "block",
        },
      },
      source:
        "goa-knowledge.md Part A 2.3 (Contribution ratio gate: UK error), Part B 3 (Margin); code pricing/thresholds.py, pricing/engine.py; help content.ts",
    },
    {
      id: "uk-carriage",
      title: "Carriage by order value",
      status: "active",
      tone: "info",
      topic: "freight",
      checks:
        "A carriage line in GBP by the value of the quotation. Up to GBP 500: GBP 45. 500 to 2,000: GBP 80. 2,000 to 5,000: GBP 145. 5,000 to 10,000: GBP 195. 10,000 to 20,000: GBP 345. 20,000 to 50,000: GBP 400. Above GBP 50,000 and for blast media there is no automatic carriage.",
      why: "Freight was the most named problem with Go Autonomous, and UK sales asked to see carriage before confirming and to have it change when the value changes.",
      youSee:
        'Info in the preview: freight "Computed", for example "Freight of 80 GBP is proposed for an order value of 500.01 to 2000 GBP." Warning "Set by hand" above GBP 50,000, with a Media line, or for a quotation not in GBP.',
      whatToDo:
        "Check the freight line before the quotation goes out. Change it for heavy, bulky, light or sensitive parts, and leave it off where the delivery terms say so.",
      example:
        "A quotation worth GBP 1,250 gets GBP 80 carriage. For a heavy casting worth GBP 600 you set the real cost instead.",
      registryIds: ["pricing.freight"],
      glossaryIds: ["freight"],
      modes: {
        "pricing.freight": {
          uk: "warn",
        },
      },
      source:
        "goa-knowledge.md Part A 2.5 (Freight tiers Europe: UK GBP 45/80/145/145/195/345/400), 9.3 (European model), Part B UK (Freight), backlog M5/M12",
    },
    {
      id: "uk-minimum-order",
      title: "Minimum order value GBP 225",
      status: "planned",
      tone: "info",
      topic: "price",
      checks:
        "Whether the quotation is below the UK minimum order value of GBP 225. Below it a handling fee applies, at most GBP 75.",
      why: "About 7 % of UK orders were small orders, and raising the minimum charge earned about GBP 80,000 a year.",
      youSee: "Planned: an info note on the draft when the value is below GBP 225.",
      whatToDo:
        "Tell the customer about the minimum order value and the handling fee, as the printed quotation does.",
      example:
        "A customer asks for 2 bolts worth GBP 40. You point out the GBP 225 minimum order value.",
      registryIds: [],
      glossaryIds: ["minimum-order-value"],
      source:
        "goa-knowledge.md Part A 3 UK (Carriage), 5 (Printed UK quotation), 9.1 (Minimum order value), backlog S16",
    },
    {
      id: "uk-large-quote",
      title: "Large quotations",
      status: "active",
      tone: "warning",
      topic: "price",
      checks: "Whether the quotation value is above GBP 50,000.",
      why: "Go Autonomous sent anything above GBP 50,000 to manual handling.",
      youSee:
        'Warning: "The quotation total of 62,000.00 GBP is above the approval limit of 50,000.00 GBP."',
      whatToDo:
        "Ask your manager to approve this quotation before it goes out, with the signature your rules require.",
      example:
        "A quotation for a full set of wear parts reaches GBP 62,000. You get it approved before sending.",
      registryIds: ["gate.doa_limit"],
      glossaryIds: ["large-quotation"],
      modes: {
        "gate.doa_limit": {
          uk: "warn",
        },
      },
      source:
        "goa-knowledge.md Part A 2.3 (Delegation of authority), 5 (Printed UK quotation: valid up to GBP 10,000 without signature)",
    },
    {
      id: "uk-vat-northern-ireland",
      title: "No VAT for Northern Ireland and abroad",
      status: "planned",
      tone: "warning",
      topic: "tax",
      checks:
        "Whether the delivery goes to Northern Ireland or outside the UK. Then every line needs the no-VAT tax group (tax code 99).",
      why: "A missed tax code means a credit note and a new invoice later.",
      youSee:
        "Planned: a warning on the quotation when the delivery address is in Northern Ireland or abroad.",
      whatToDo: "Set tax code 99 on every line yourself, as today.",
      example: "A customer in Belfast asks for 5 parts. You put all 5 lines on tax code 99.",
      registryIds: [],
      glossaryIds: [],
      source: "goa-knowledge.md Part A 2.5 (UK VAT outside GB), Part B UK (Tax), backlog M11",
    },
    {
      id: "uk-lead-time",
      title: "Delivery time from Czechia",
      status: "active",
      tone: "warning",
      topic: "price",
      checks:
        "The delivery time from F&O. Parts that Czechia makes and does not keep in stock take 27 working days, plus 5 to 7 working days transport.",
      why: "UK sales gave wrong dates when stock and Czech delivery times were not visible.",
      youSee:
        'Warning: "Line 2 has a lead time of 120 days." or a ship date after the requested date. I2Q does not have the F&O lead times yet, so you rarely see these warnings today. The check for a price without a lead time is off.',
      whatToDo:
        "Check stock and delivery time yourself. Offer a part delivery if some parts are ready earlier.",
      example:
        "The customer needs a part in 2 weeks, but it comes from Czechia without stock. You quote 27 working days plus transport.",
      plannedPart: "Lead times from F&O, so these warnings show reliably.",
      registryIds: ["pricing.lead_time", "pricing.after_requested", "pricing.ship_today"],
      glossaryIds: ["lead-time-warnings"],
      modes: {
        "pricing.lead_time": {
          uk: "warn",
        },
        "pricing.after_requested": {
          uk: "warn",
        },
        "pricing.ship_today": {
          uk: "off",
        },
      },
      source:
        "goa-knowledge.md Part A 2.3 (Lead time: UK CZ non-stock 27 working days), Part B UK (Lead time); help content.ts (not-yet)",
    },
    {
      id: "uk-sales-pool",
      title: "Sales pool comes from the part",
      status: "active",
      tone: "warning",
      topic: "quotation",
      checks:
        "The sales pool on the quotation header: AIR, WHE, MED or Striko, from the item group with the most lines. Impact item groups do not count, so the pool is never Impact.",
      why: "Go Autonomous set every UK quotation to Impact, and sales had to correct it by hand every time.",
      youSee:
        'The quotation preview shows the proposed sales pool. Warning when two item groups tie or a group has no pool, for example "Item group ... has no sales pool for this entity."',
      whatToDo:
        "Check the pool in F&O. Where I2Q proposes none, set it from the main business line of the parts.",
      example: "A quotation for shot blast wheel parts gets pool WHE, not Impact.",
      registryIds: ["header.pool"],
      glossaryIds: ["quotation-header"],
      modes: {
        "header.pool": {
          uk: "warn",
        },
      },
      source:
        "goa-knowledge.md Part A 2.4 (Item group to CRM fields to sales pool: UK AIR / WHE / MED / Striko), Part B UK (Header); code fo/intent.py (salesPoolId None)",
    },
    {
      id: "uk-impact-item-groups",
      title: "Impact item groups are outside parts sales",
      status: "active",
      tone: "warning",
      topic: "items",
      checks: "Whether a line is in an item group starting with IMP_ (Impact).",
      why: "Impact groups do not belong to UK parts sales, so they must not decide the sales pool.",
      youSee:
        'Warning: "Line 2 is in item group ..., which is outside parts sales." The line does not count when I2Q proposes the sales pool.',
      whatToDo: "Check with your manager whether the UK quotes this line.",
      example:
        "A quotation has 3 wear parts and 1 Impact line. The Impact line gets the warning, and the pool follows the wear parts, not Impact.",
      registryIds: ["gate.out_of_scope_group"],
      glossaryIds: ["out-of-scope-item-groups"],
      modes: {
        "gate.out_of_scope_group": {
          uk: "warn",
        },
      },
      source:
        "rules/data/manual_item_groups.csv (uk IMP_ out_of_scope); goa-knowledge.md UK pool Impact default",
    },
    {
      id: "uk-sales-unit",
      title: "Sales unit and leavers",
      status: "active",
      tone: "info",
      topic: "quotation",
      checks:
        "The customer's sales unit: who is responsible for the account and who takes the order.",
      why: "UK sales units still pointed to people who had left, so quotations went out under the wrong name.",
      youSee:
        'Info: "Sales unit, sales responsible, sales taker, payment terms, delivery terms and delivery mode are not available yet." I2Q does not have the customer\'s sales unit yet.',
      whatToDo:
        'Check the name under "our reference" before you send. Report a wrong sales unit so it gets fixed in F&O.',
      example:
        "The account still shows a colleague who left in spring. You put yourself in and tell the team lead.",
      plannedPart:
        "The sales unit on the preview, with a warning when it is missing or points to a leaver.",
      registryIds: ["header.not_available"],
      glossaryIds: ["quotation-header"],
      modes: {
        "header.not_available": {
          uk: "info",
        },
      },
      source:
        "goa-knowledge.md Part A 2.4 (Sales unit: UK 8 units, 01-01 failed), Part B UK (Header), backlog M10",
    },
    {
      id: "uk-header-fields",
      title: "What the UK quotation shows",
      status: "active",
      tone: "info",
      topic: "quotation",
      checks:
        'The fields of the printed UK quotation. At the top: quotation number (SQ04xxxx-1, the ending is the version), customer number, contact, date, our reference and your reference. Per line: net weight, country of origin, commodity code and "Lead time EXW n days". At the end: carriage, Incoterms, mode of delivery and payment terms. A UK quotation is valid up to GBP 10,000 without a signature.',
      why: "These are the fields UK customers expect and the ones sales filled or corrected by hand.",
      youSee:
        'Info. "Prepare quotation" shows a read-only preview with customer, currency, lines, freight, sales pool, language and customer reference. Payment terms, delivery terms, validity and sales unit are not on it yet.',
      whatToDo: "Create the quotation in F&O as today and check these fields there.",
      example:
        "The preview shows 3 lines in GBP. You add carriage and check the payment terms in F&O.",
      registryIds: ["header.pool", "header.not_available"],
      glossaryIds: ["quotation-header"],
      modes: {
        "header.pool": {
          uk: "warn",
        },
        "header.not_available": {
          uk: "info",
        },
      },
      source:
        "goa-knowledge.md Part A 5 (Numbering, Printed UK quotation, I2Q today); code fo/intent.py",
    },
    {
      id: "uk-customer-reference",
      title: "Your reference field",
      status: "active",
      tone: "info",
      topic: "quotation",
      checks:
        "The customer reference on the quotation: the customer's own inquiry number from the mail, up to 30 characters.",
      why: "Go Autonomous left it empty on UK quotations while manual ones held the customer name, so quotes were hard to find later.",
      youSee:
        'Info: the inquiry number as "Customer reference" on the quotation preview. If the mail has none, the field stays empty.',
      whatToDo:
        "Check the reference. If the mail has no inquiry number, fill in what the customer uses, as today.",
      example: 'The mail says "RFQ 45821". You enter 45821 as your reference.',
      registryIds: [],
      glossaryIds: ["customer-reference-fields"],
      source:
        "goa-knowledge.md Part A 2.5 (Customer requisition / reference), Part B UK (Header), backlog S3",
    },
    {
      id: "uk-terms",
      title: "Payment and delivery terms",
      status: "planned",
      tone: "info",
      topic: "mail-and-case",
      checks:
        "Payment terms, delivery terms and mode of delivery from the customer account, unless the inquiry asks for something else.",
      why: "Go Autonomous had an export delivery terms check for the UK that never worked because the terms were missing in its data.",
      youSee: "Planned: the customer's terms on the preview.",
      whatToDo:
        "Check the terms in F&O. Customers paying in advance need a pro-forma with carriage.",
      example:
        "A customer on cash in advance asks for a price. You send a pro-forma with carriage included.",
      registryIds: [],
      glossaryIds: [],
      source:
        "goa-knowledge.md Part A 2.5 (Payment terms, Delivery terms / mode), 9.3 (prepayment customers), backlog M2",
    },
    {
      id: "uk-language-currency",
      title: "Language, currency and validity",
      status: "active",
      tone: "info",
      topic: "quotation",
      checks:
        "The reply draft follows the language of the customer's mail. The currency comes from the customer account (GBP as a code, not the £ sign). The UK quotation is in British English and valid for one month.",
      why: "Go Autonomous sent the £ sign instead of the currency code, and all countries printed in American English until the language was fixed.",
      youSee:
        "Info: the reply draft is in the customer's language, and the currency comes from the account. The preview shows the document language, English (UK) unless the customer has another.",
      whatToDo: 'Keep "valid until one month after date of issue" on the quotation, as today.',
      example:
        "A French customer writes to the UK in French. The draft reply is in French, the quotation in GBP.",
      plannedPart: "The validity on the quotation preview.",
      registryIds: [],
      glossaryIds: ["translation", "currency", "quotation-validity"],
      source:
        "goa-knowledge.md Part A 2.3 (Currency, bug 36), 2.5 (Quote document language: UK en-gb; Validity), 9.1 (Default language)",
    },
    {
      id: "uk-quotation-texts",
      title: "Machine, serial number and visit texts",
      status: "planned",
      tone: "info",
      topic: "mail-and-case",
      checks:
        "Notes about the machine, its serial number or an engineer visit that belong on the quotation.",
      why: "In Go Autonomous these texts had to be added before the document was made, or they were lost.",
      youSee: "Planned: a place for these texts before the quotation is prepared.",
      whatToDo: "Add the machine and serial number to the quotation in F&O yourself.",
      example: "The customer names machine serial 12345. You put it in the quotation text.",
      registryIds: [],
      glossaryIds: [],
      source:
        "goa-knowledge.md Part A 5 (Quote text), Part B 5 (M17: UK machine, serial and visit text)",
    },
    {
      id: "uk-urgent",
      title: '"Next day delivery" is not always urgent',
      status: "planned",
      tone: "info",
      topic: "mail-and-case",
      checks: 'Words such as "urgent" or "machine down" in the mail.',
      why: 'UK customer forms print "next day delivery" on every PO, so Go Autonomous marked far too many mails as urgent.',
      youSee: "Planned: an urgent tag that ignores this standard text.",
      whatToDo: "Read the mail yourself to judge how urgent it is.",
      example:
        'A routine price request carries "next day delivery" in the footer. It is not treated as urgent.',
      registryIds: [],
      glossaryIds: [],
      source: "goa-knowledge.md Part A 2.5 (Urgency tags), Part B 3 (Urgency)",
    },
    {
      id: "uk-you-send",
      title: "You check and you send",
      status: "active",
      tone: "info",
      topic: "mail-and-case",
      checks: "Nothing. I2Q never sends mail and never creates the quotation in F&O.",
      why: "In Go Autonomous the UK team checked every quotation before it went out, and that stays the rule.",
      youSee: 'Info. "Create quotation in the order system" is switched off.',
      whatToDo: "Copy the reply text, create the quotation in F&O and send it yourself.",
      example: "You copy the draft reply into Outlook, attach the F&O quotation and send.",
      registryIds: [],
      glossaryIds: ["nothing-is-sent"],
      source:
        "docs/scope.md; goa-knowledge.md Part A 9.3 (Reply path), Part B 3 (Sending); help content.ts (not-yet)",
    },
  ],
};
